import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { AppLayout } from '../components/AppLayout';
import {
  MapPin, CheckCircle, XCircle, AlertTriangle, Camera,
  FileText, Send, Edit, ArrowLeft, Loader2, Clock, MessageSquare,
  Download, Mail, Plus, Trash2, Users, X, ChevronLeft, ChevronRight, ZoomIn,
  ClipboardCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function StatusIcon({ value }) {
  if (value === 'Oui') return <CheckCircle className="w-4 h-4 text-emerald-500" />;
  if (value === 'Non') return <XCircle className="w-4 h-4 text-red-500" />;
  if (value === 'Partiellement') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  return <span className="w-4 h-4 text-slate-300">—</span>;
}

function StatusBadge({ value }) {
  if (!value) return <Badge variant="outline" className="text-xs text-slate-400">Non renseigné</Badge>;
  const colors = {
    Oui: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    Non: 'bg-red-100 text-red-700 border-red-300',
    Partiellement: 'bg-amber-100 text-amber-700 border-amber-300',
  };
  return <Badge className={`text-xs border ${colors[value] || 'bg-slate-100 text-slate-600 border-slate-300'}`}>{value}</Badge>;
}

export default function ChantierSynthesePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user, isAdmin } = useAuth();
  const [ch, setCh] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [diffusing, setDiffusing] = useState(false);
  const [checklistLabels, setChecklistLabels] = useState({});
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Diffuser modal state
  const [showDiffuserModal, setShowDiffuserModal] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  // PV de livraison link state
  const [linkedPv, setLinkedPv] = useState(null); // { exists, pv?, commande_corner_id, message? }
  const [openingPv, setOpeningPv] = useState(false);

  useEffect(() => {
    if (!token || !id) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/pv-livraison/by-chantier/${id}`, { headers });
        if (r.ok) setLinkedPv(await r.json());
      } catch { /* silent */ }
    })();
  }, [token, id]);

  const openLinkedPv = async () => {
    if (!linkedPv) return;
    if (!linkedPv.commande_corner_id) {
      toast.warning(linkedPv.message || 'Aucune commande corner liée à ce chantier');
      return;
    }
    setOpeningPv(true);
    try {
      navigate(`/pv-livraison/${linkedPv.commande_corner_id}`);
    } finally {
      setOpeningPv(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [chRes, clRes] = await Promise.all([
          fetch(`${API}/api/chantiers/${id}`, { headers }),
          fetch(`${API}/api/chantier-checklist-items`, { headers }),
        ]);
        if (chRes.ok) setCh(await chRes.json());
        else toast.error('Chantier non trouvé');
        if (clRes.ok) {
          const items = await clRes.json();
          const map = {};
          items.forEach(it => { map[it.key] = it.label; });
          setChecklistLabels(map);
        }
      } catch { toast.error('Erreur réseau'); }
      finally { setLoading(false); }
    })();
  }, [id, token]);

  const openDiffuserModal = async () => {
    setLoadingRecipients(true);
    setShowDiffuserModal(true);
    try {
      const res = await fetch(`${API}/api/chantiers/${id}/diffuser-pv/recipients`, { headers });
      if (res.ok) {
        const data = await res.json();
        setRecipients(data.recipients || []);
      } else {
        toast.error('Erreur lors du chargement des destinataires');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setLoadingRecipients(false); }
  };

  const addRecipient = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Adresse email invalide');
      return;
    }
    if (recipients.includes(email)) {
      toast.error('Ce destinataire est déjà dans la liste');
      return;
    }
    setRecipients(prev => [...prev, email]);
    setNewEmail('');
  };

  const removeRecipient = (email) => {
    setRecipients(prev => prev.filter(e => e !== email));
  };

  const handleDiffuser = async () => {
    if (recipients.length === 0) {
      toast.error('Ajoutez au moins un destinataire');
      return;
    }
    setDiffusing(true);
    try {
      const res = await fetch(`${API}/api/chantiers/${id}/diffuser-pv`, {
        method: 'POST', headers,
        body: JSON.stringify({ recipients }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'PV diffusé avec succès');
        setShowDiffuserModal(false);
        const r2 = await fetch(`${API}/api/chantiers/${id}`, { headers });
        if (r2.ok) setCh(await r2.json());
      } else {
        let msg = 'Erreur lors de la diffusion';
        try { const err = await res.json(); msg = err.detail || msg; } catch {}
        toast.error(msg);
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setDiffusing(false); }
  };

  if (loading) return <AppLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div></AppLayout>;
  if (!ch) return <AppLayout><div className="text-center py-16 text-slate-500">Chantier non trouvé</div></AppLayout>;

  const checklist = ch.checklist || {};
  const comments = ch.checklist_comments || {};
  const photos = (ch.photos || []).filter(p => !p.is_deleted);

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6 pb-8" data-testid="chantier-synthese">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/chantiers')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <h1 className="text-xl font-bold text-slate-800">Synthèse du chantier</h1>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={ch.status === 'soumis' ? 'default' : ch.status === 'valide' ? 'outline' : 'secondary'} className="text-sm">
              {ch.status || 'brouillon'}
            </Badge>
            {ch.pv_diffuse && (
              <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs">PV diffusé</Badge>
            )}
            {linkedPv && linkedPv.commande_corner_id && (
              <Button
                size="sm"
                variant="outline"
                onClick={openLinkedPv}
                disabled={openingPv}
                className={`gap-1 ${linkedPv.exists ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50'}`}
                data-testid="open-linked-pv-btn"
                title={linkedPv.exists ? 'Ouvrir le PV de livraison signé/brouillon' : 'Créer/ouvrir le PV de livraison pour cette commande corner'}
              >
                {openingPv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardCheck className="w-3.5 h-3.5" />}
                {linkedPv.exists ? 'Voir le PV de livraison' : 'Ouvrir / créer le PV'}
                {linkedPv.exists && linkedPv.pv?.status === 'signed' && (
                  <span className="ml-1 text-[10px] bg-emerald-600 text-white px-1 rounded">SIGNÉ</span>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* 1. Identification */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4 text-orange-500" /> Identification</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <div><span className="text-slate-500">Enseigne</span><p className="font-medium">{ch.enseigne || '-'}</p></div>
              <div><span className="text-slate-500">Nom du magasin</span><p className="font-medium">{ch.nom_magasin || '-'}</p></div>
              <div><span className="text-slate-500">Adresse</span><p className="font-medium">{ch.adresse || '-'} {ch.code_postal || ''} {ch.ville || ''}</p></div>
              <div><span className="text-slate-500">Nature du projet</span><p className="font-medium">{ch.nature_projet || '-'}</p></div>
              <div><span className="text-slate-500">Marque(s)</span><p className="font-medium">{(ch.marques || []).join(', ') || '-'}</p></div>
              <div><span className="text-slate-500">Intervenant(s)</span><p className="font-medium">{(ch.intervenants_selected || []).join(', ') || ch.intervenants || '-'}</p></div>
            </div>
          </CardContent>
        </Card>

        {/* 2. Checklist complet */}
        {Object.keys(checklist).length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><CheckCircle className="w-4 h-4 text-orange-500" /> Vérifications chantier</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {Object.entries(checklist).map(([key, value]) => (
                  <div key={key} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
                    <StatusIcon value={value} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-slate-700">{checklistLabels[key] || key}</span>
                        <StatusBadge value={value} />
                      </div>
                      {comments[key] && (
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> {comments[key]}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 3. Photos */}
        {photos.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Camera className="w-4 h-4 text-orange-500" /> Photos ({photos.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {photos.map((photo, idx) => {
                  const photoUrl = photo.url || `${API}/api/chantiers/${id}/photos/${photo.id}?auth=${token}`;
                  return (
                    <div key={idx} className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50 cursor-pointer"
                      onClick={() => setLightboxIdx(idx)} data-testid={`photo-thumb-${idx}`}>
                      <img src={photoUrl} alt={photo.description || `Photo ${idx + 1}`}
                        className="w-full h-40 object-cover transition-transform group-hover:scale-105" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                      </div>
                      <div className="p-2 text-xs text-slate-600">
                        <p className="font-medium truncate">{photo.description || photo.original_filename || `Photo ${idx + 1}`}</p>
                        {photo.step && <p className="text-slate-400">Étape {photo.step}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Photo Lightbox */}
        {lightboxIdx >= 0 && lightboxIdx < photos.length && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" data-testid="photo-lightbox"
            onClick={() => setLightboxIdx(-1)}>
            {/* Close button */}
            <button className="absolute top-4 right-4 text-white/80 hover:text-white z-10" onClick={() => setLightboxIdx(-1)}>
              <X className="w-8 h-8" />
            </button>
            {/* Counter */}
            <div className="absolute top-4 left-4 text-white/80 text-sm font-medium">
              {lightboxIdx + 1} / {photos.length}
            </div>
            {/* Previous */}
            {lightboxIdx > 0 && (
              <button className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-2 text-white z-10"
                onClick={e => { e.stopPropagation(); setLightboxIdx(lightboxIdx - 1); }}>
                <ChevronLeft className="w-8 h-8" />
              </button>
            )}
            {/* Next */}
            {lightboxIdx < photos.length - 1 && (
              <button className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-2 text-white z-10"
                onClick={e => { e.stopPropagation(); setLightboxIdx(lightboxIdx + 1); }}>
                <ChevronRight className="w-8 h-8" />
              </button>
            )}
            {/* Image */}
            <div className="max-w-[90vw] max-h-[85vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
              <img
                src={photos[lightboxIdx].url || `${API}/api/chantiers/${id}/photos/${photos[lightboxIdx].id}?auth=${token}`}
                alt={photos[lightboxIdx].description || `Photo ${lightboxIdx + 1}`}
                className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
              />
              <div className="mt-3 text-center">
                <p className="text-white font-medium">{photos[lightboxIdx].description || photos[lightboxIdx].original_filename || `Photo ${lightboxIdx + 1}`}</p>
                {photos[lightboxIdx].step && <p className="text-white/60 text-sm">Étape {photos[lightboxIdx].step}</p>}
              </div>
            </div>
          </div>
        )}

        {/* 4. Observations & Validation */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-orange-500" /> Observations & Validation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {ch.observations && (
              <div>
                <span className="text-xs text-slate-500">Observations</span>
                <p className="text-sm mt-0.5 p-2 bg-slate-50 rounded-lg">{ch.observations}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <div><span className="text-slate-500">Repassage prévu</span><p className="font-medium">{ch.repassage_prevu || '-'}</p></div>
              {ch.repassage_prevu === 'Oui' && ch.date_repassage && (
                <div><span className="text-slate-500">Date repassage</span><p className="font-medium">{ch.date_repassage}</p></div>
              )}
              <div><span className="text-slate-500">Date de validation</span><p className="font-medium">{ch.date_validation || '-'}</p></div>
              <div><span className="text-slate-500">Signataire</span><p className="font-medium">{ch.nom_signataire || '-'}</p></div>
            </div>
            {ch.signature_data && ch.signature_data.startsWith('data:') && (
              <div>
                <span className="text-xs text-slate-500">Signature</span>
                <div className="mt-1 border rounded-lg p-2 bg-white inline-block">
                  <img src={ch.signature_data} alt="Signature" className="h-16" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 5. Historique */}
        {(ch.history || []).length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-orange-500" /> Historique</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {(ch.history || []).slice(-10).reverse().map((h, idx) => (
                  <div key={idx} className="flex items-center gap-3 py-1.5 text-xs border-b border-slate-50 last:border-0">
                    <span className="text-slate-400 font-mono whitespace-nowrap">{(h.timestamp || '').slice(0, 16).replace('T', ' ')}</span>
                    <span className="font-medium text-slate-600">{h.user_name}</span>
                    <span className="text-slate-500">{h.details}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between p-4 bg-white border rounded-xl shadow-sm" data-testid="synthese-actions">
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`/chantier/${id}/edit`)} data-testid="edit-chantier-btn">
              <Edit className="w-4 h-4 mr-2" /> Modifier
            </Button>
            <Button variant="outline" onClick={async () => {
              try {
                const res = await fetch(`${API}/api/chantiers/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
                if (res.ok) {
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url;
                  a.download = `PV_Livraison_${(ch.nom_magasin || 'chantier').replace(/\s/g, '_')}.pdf`;
                  a.click(); URL.revokeObjectURL(url);
                } else toast.error('Erreur lors du téléchargement');
              } catch { toast.error('Erreur réseau'); }
            }} data-testid="download-pdf-btn">
              <Download className="w-4 h-4 mr-2" /> Télécharger PDF
            </Button>
          </div>
          {isAdmin && (
            <Button onClick={openDiffuserModal} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="diffuser-pv-btn">
              <Send className="w-4 h-4 mr-2" />
              Diffuser le PV de livraison
            </Button>
          )}
        </div>
      </div>

      {/* Diffuser PV Modal */}
      <Dialog open={showDiffuserModal} onOpenChange={setShowDiffuserModal}>
        <DialogContent className="sm:max-w-lg" data-testid="diffuser-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" />
              Destinataires du PV de livraison
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">
              Vérifiez la liste des destinataires avant d'envoyer le PV. Vous pouvez ajouter ou supprimer des adresses.
            </p>

            {loadingRecipients ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
              </div>
            ) : (
              <>
                {/* Recipients list */}
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {recipients.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">Aucun destinataire configuré</p>
                  )}
                  {recipients.map((email) => (
                    <div key={email} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-100" data-testid={`recipient-${email}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-sm text-slate-700 truncate">{email}</span>
                      </div>
                      <button
                        onClick={() => removeRecipient(email)}
                        className="text-slate-400 hover:text-red-500 transition-colors flex-shrink-0 ml-2"
                        data-testid={`remove-recipient-${email}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new email */}
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Ajouter un email..."
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
                    data-testid="add-recipient-input"
                  />
                  <Button variant="outline" size="sm" onClick={addRecipient} className="flex-shrink-0" data-testid="add-recipient-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                <p className="text-xs text-slate-400">{recipients.length} destinataire(s)</p>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDiffuserModal(false)} data-testid="cancel-diffuser-btn">
              Annuler
            </Button>
            <Button
              onClick={handleDiffuser}
              disabled={diffusing || recipients.length === 0}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="confirm-diffuser-btn"
            >
              {diffusing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer à {recipients.length} destinataire(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
